import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, Newspaper, Users, GraduationCap, Sparkles, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import KhutaSadaDialog from "../components/KhutaSadaDialog";
import KhutaSadaTrackerDialog from "../components/KhutaSadaTrackerDialog";
import WelcomeAnnouncement from "../components/WelcomeAnnouncement";
import KhutaCountdown from "../components/KhutaCountdown";
import RegistrationAnnouncementBar from "../components/RegistrationAnnouncementBar";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [khutaEmail, setKhutaEmail] = useState(null);

  useEffect(() => {
    try {
      setKhutaEmail(localStorage.getItem("khuta_sada_email"));
    } catch (_) {}
    const onReg = () => {
      try {
        setKhutaEmail(localStorage.getItem("khuta_sada_email"));
      } catch (_) {}
    };
    window.addEventListener("khuta-sada-registered", onReg);
    return () => window.removeEventListener("khuta-sada-registered", onReg);
  }, []);

  const resetKhutaTracker = () => {
    try {
      localStorage.removeItem("khuta_sada_email");
    } catch (_) {}
    setKhutaEmail(null);
  };

  useEffect(() => {
    fetchNews();
    fetchEvents();
    fetchSettings();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data.slice(0, 3));
    } catch (error) {
      console.error("Error fetching news:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data.slice(0, 3));
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const heroImage = settings?.hero_image || 'https://static.prod-images.emergentagent.com/jobs/a2beddaf-b4e7-471a-98d4-6b313b1f91ba/images/7c8970b93953d761dd3ee5d6f036a1c10c6b2bcb51a931769e354f550cc5b93f.png';
  const aboutImage = settings?.about_image || 'https://customer-assets.emergentagent.com/job_school-legacy/artifacts/lt30qar7_PHOTO-2024-09-19-11-44-58.jpg';

  return (
    <div className="min-h-screen overflow-x-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <WelcomeAnnouncement />
      <Header topBar={<RegistrationAnnouncementBar />} />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-gradient"></div>
        <div className="relative z-10 text-center px-6 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}} data-testid="hero-title">
              {t('home.heroTitle')}
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto" data-testid="hero-subtitle">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="btn-accent text-lg px-8 py-3" data-testid="hero-cta-register">
                {t('home.registerCta')}
              </Link>
              <Link to="/directory" className="bg-white text-[#0B132B] px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors text-lg" data-testid="hero-cta-directory">
                {t('home.viewDirectory')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* عن رابطة أنجال — Single centered Arabic intro (formal & elegant) */}
      <section
        className="relative py-20 px-6 overflow-hidden bg-white border-b border-[#E2E8F0]"
        data-testid="intro-anjal-section"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          dir="rtl"
          className="relative max-w-3xl mx-auto text-center"
        >
          {/* Simple gold divider */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 sm:w-16 bg-[#D4AF37]" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
            <div className="h-px w-12 sm:w-16 bg-[#D4AF37]" />
          </div>

          <p className="text-[11px] sm:text-xs uppercase tracking-[0.3em] font-semibold text-[#D4AF37] mb-4">
            رابطة أنجال
          </p>

          <h2
            className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold mb-8 text-[#0B132B] leading-tight tracking-tight"
            style={{ fontFamily: 'Cairo', textWrap: 'balance' }}
          >
            عن رابطة أنجال
          </h2>

          <p
            className="text-base sm:text-lg text-[#334155] mx-auto max-w-2xl"
            style={{
              fontFamily: 'Cairo',
              lineHeight: 2.1,
              letterSpacing: 0,
              wordSpacing: '0.04em',
            }}
          >
            رابطة أنجال لخريجات مدارس أنجال التربية التعليمية هي منصة تجمع خريجات أنجال
            لتعزيز التواصل، مشاركة الخبرات، دعم الطالبات، وبناء مجتمع ملهم من الخريجات.
          </p>

          {/* Bottom gold divider */}
          <div className="flex items-center justify-center gap-3 mt-10">
            <div className="h-px w-12 sm:w-16 bg-[#D4AF37]" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
            <div className="h-px w-12 sm:w-16 bg-[#D4AF37]" />
          </div>
        </motion.div>

        {/* Hidden SEO keywords for crawlers — not visually shown */}
        <p className="sr-only" aria-hidden="true">
          رابطة أنجال — رابطة خريجات أنجال — خريجات أنجال — مدارس أنجال التربية التعليمية —
          Anjal Alumni Association — Anjal Alumni — Anjal School Alumni — Anjal Education Schools.
        </p>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src={aboutImage}
                alt="Campus Life" 
                className="rounded-lg shadow-xl w-full h-[400px] object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4">{t('home.aboutLabel')}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B132B] mb-6 leading-tight" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>
                {t('home.aboutTitle')}
              </h2>
              <p className="text-base text-[#475569] leading-relaxed mb-6">
                {t('home.aboutText1')}
              </p>
              <p className="text-base text-[#475569] leading-relaxed mb-8">
                {t('home.aboutText2')}
              </p>
              <Link to="/programs" className="btn-primary" data-testid="about-cta">
                {t('home.explorePrograms')}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Users className="mx-auto mb-4 text-[#D4AF37]" size={48} />
              <h3 className="text-4xl font-bold text-[#0B132B] mb-2" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>3000+</h3>
              <p className="text-[#475569]">{t('home.stats.alumni')}</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <GraduationCap className="mx-auto mb-4 text-[#D4AF37]" size={48} />
              <h3 className="text-4xl font-bold text-[#0B132B] mb-2" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>24+</h3>
              <p className="text-[#475569]">{t('home.stats.years')}</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Calendar className="mx-auto mb-4 text-[#D4AF37]" size={48} />
              <h3 className="text-4xl font-bold text-[#0B132B] mb-2" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>17+</h3>
              <p className="text-[#475569]">{t('home.stats.events')}</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Newspaper className="mx-auto mb-4 text-[#D4AF37]" size={48} />
              <h3 className="text-4xl font-bold text-[#0B132B] mb-2" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>10+</h3>
              <p className="text-[#475569]">{t('home.stats.programs')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Recent News */}
      <section className="py-20 px-6 bg-white" data-testid="news-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-2">{t('home.latestNews')}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B132B]" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>{t('home.recentNews')}</h2>
            </div>
            <Link to="/news" className="text-[#1E3A8A] font-semibold hover:text-[#D4AF37] transition-colors" data-testid="view-all-news">{t('home.viewAll')} {isRTL ? '←' : '→'}</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {news.length > 0 ? news.map((item, index) => (
              <motion.div 
                key={item.id}
                className="border border-[#E2E8F0] rounded-lg overflow-hidden card-hover"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`news-card-${index}`}
              >
                <div className="p-6">
                  <p className="text-xs text-[#D4AF37] font-semibold mb-2">{item.published_date}</p>
                  <h3 className="text-xl font-semibold text-[#0B132B] mb-3" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>{item.title}</h3>
                  <p className="text-[#475569] mb-4 line-clamp-3">{item.content}</p>
                  <p className="text-sm text-[#475569]">{t('news.by')} {item.author}</p>
                </div>
              </motion.div>
            )) : (
              <p className="text-[#475569] col-span-3 text-center">{t('news.noNews')}</p>
            )}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-20 px-6 bg-[#F8FAFC]" data-testid="events-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-2">{t('home.upcomingEvents')}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B132B]" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>{t('home.upcomingEventsTitle')}</h2>
            </div>
            <Link to="/events" className="text-[#1E3A8A] font-semibold hover:text-[#D4AF37] transition-colors" data-testid="view-all-events">{t('home.viewAll')} {isRTL ? '←' : '→'}</Link>
          </div>

          {/* Featured: للخطى صدى — Custom Emerald/Gold banner with expressive imagery */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative mb-10 rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #083344 0%, #164E63 45%, #0E7490 100%)',
              boxShadow: '0 20px 60px -20px rgba(6, 78, 59, 0.55)',
            }}
            data-testid="khuta-sada-card"
            dir="rtl"
          >
            {/* Expressive imagery — books / notebook / writing (legacy & learning symbolism) */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage:
                  "url('https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1600&q=80')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.18,
                mixBlendMode: 'luminosity',
              }}
            />
            {/* Dark emerald tint overlay for legibility */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'linear-gradient(135deg, rgba(2,44,34,0.92) 0%, rgba(22,78,99,0.78) 50%, rgba(6,95,70,0.86) 100%)',
              }}
            />

            {/* Gold corner accents */}
            <div
              aria-hidden
              className="absolute -top-20 -left-20 w-72 h-72 rounded-full blur-3xl opacity-25 pointer-events-none"
              style={{ background: 'radial-gradient(circle, #F5C86B 0%, transparent 70%)' }}
            />
            <div
              aria-hidden
              className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-20 pointer-events-none"
              style={{ background: 'radial-gradient(circle, #F5C86B 0%, transparent 70%)' }}
            />
            {/* Gold top & bottom hairlines */}
            <div aria-hidden className="absolute top-0 inset-x-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #F5C86B, transparent)' }} />
            <div aria-hidden className="absolute bottom-0 inset-x-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #F5C86B, transparent)' }} />

            <div className="relative p-6 sm:p-8 md:p-12">
              <div className="max-w-3xl">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={16} style={{ color: '#F5C86B' }} />
                  <span className="text-[11px] uppercase tracking-[0.3em] font-semibold" style={{ color: '#F5C86B' }}>
                    فعالية جديدة
                  </span>
                </div>
                <h3
                  className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight"
                  style={{ fontFamily: 'Cairo' }}
                >
                  للخطى صدى
                </h3>
                <p className="text-[#ECFEFF] text-base sm:text-lg leading-loose mb-6" style={{ lineHeight: 2 }}>
                  لقاءات وورش وبرامج تقدّمها خريجات أنجال لطالبات المدرسة، بالتنسيق مع المدرسة.
                </p>
                <div className="space-y-2 text-sm text-[#CFFAFE]">
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 inline-block w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: '#F5C86B' }} />
                    <span>
                      <span className="font-semibold text-white">فترة التسجيل:</span> من ١٨ أكتوبر إلى ١ نوفمبر ٢٠٢٦.
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 inline-block w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: '#F5C86B' }} />
                    <span>
                      <span className="font-semibold text-white">فترة الزيارات:</span> من ٣ نوفمبر إلى ١٠ ديسمبر ٢٠٢٦.
                    </span>
                  </div>
                </div>

                {/* Gold countdown to registration deadline */}
                <KhutaCountdown />

                <div className="mt-6">
                  {khutaEmail ? (
                    <KhutaSadaTrackerDialog
                      email={khutaEmail}
                      onReset={resetKhutaTracker}
                      trigger={
                        <Button
                          className="h-14 px-8 text-base font-bold rounded-full gap-2 shadow-xl"
                          style={{ backgroundColor: '#F5C86B', color: '#083344' }}
                          data-testid="khuta-tracker-btn"
                        >
                          تتبع طلبي
                          <ArrowLeft size={18} />
                        </Button>
                      }
                    />
                  ) : (
                    <KhutaSadaDialog
                      trigger={
                        <Button
                          className="h-14 px-8 text-base font-bold rounded-full gap-2 shadow-xl"
                          style={{ backgroundColor: '#F5C86B', color: '#083344' }}
                          data-testid="khuta-register-btn"
                        >
                          التسجيل للمشاركة
                          <ArrowLeft size={18} />
                        </Button>
                      }
                    />
                  )}
                  <a
                    href="/khuta-sada"
                    className="inline-block mt-4 text-sm font-semibold transition-colors"
                    style={{ color: '#F5C86B' }}
                    data-testid="khuta-details-link"
                  >
                    تفاصيل البرنامج ←
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {events.length > 0 ? events.map((event, index) => (
              <motion.div 
                key={event.id}
                className="bg-white border border-[#E2E8F0] rounded-lg p-6 card-hover"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`event-card-${index}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="text-[#1E3A8A]" size={24} />
                  <p className="text-sm text-[#D4AF37] font-semibold">{event.date}</p>
                </div>
                <h3 className="text-xl font-semibold text-[#0B132B] mb-3" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>{event.title}</h3>
                <p className="text-[#475569] mb-4">{event.description}</p>
                <p className="text-sm text-[#475569]">{event.location}</p>
              </motion.div>
            )) : (
              <p className="text-[#475569] col-span-3 text-center">{t('events.noEvents')}</p>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#0B132B]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-6" style={{fontFamily: isRTL ? 'Cairo' : 'Cormorant Garamond'}}>
            {t('home.ctaTitle')}
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            {t('home.ctaText')}
          </p>
          <Link to="/register" className="btn-accent text-lg px-8 py-3" data-testid="cta-register-button">
            {t('home.registerToday')}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
